import type { ColumnDef } from "@tanstack/react-table";

import { Copy } from "lucide-react";

import { LeadRequestStatus } from "@/entities/lead-claim-request/model";
import type { ModeratorLeadTable } from "@/entities/lead-v2";
import { SortButton } from "@/features/SortButton/SortButton";
import { ActionsDropdown } from "@/pages/restricted/moderator/ui";
import { useToast } from "@/shared/hooks/useToast";
import { formatDate } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import { DataTableCenteredCell } from "@/shared/ui/DataTable";
import { LeadRequestStatusModal } from "@/widgets/LeadRequestStatusModal";
import { ProofsModal } from "@/widgets/ProofsModal";

export const LeadsColumns: ColumnDef<ModeratorLeadTable>[] = [
  {
    accessorKey: "worker",
    header: () => <DataTableCenteredCell>Worker</DataTableCenteredCell>,
    cell: ({ row }) => {
      const { toast } = useToast();

      const handleCopy = () => {
        navigator.clipboard.writeText(row.original?.user?.username!);

        toast({
          title: "Успешно!",
          description: "Телеграм ID скопирован в буфер обмена.",
        });
      };

      return (
        <div className="flex items-center justify-center gap-2">
          <span className="text-textColor">{row.original?.user?.username}</span>
          <Button variant="ghost" size="icon" onClick={handleCopy}>
            <Copy className="stroke-textColor" size={16} />
          </Button>
        </div>
      );
    },
  },
  {
    accessorKey: "tgUsername",
    header: () => <DataTableCenteredCell>Username</DataTableCenteredCell>,
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.leadTelegramUsername
          ? `@${row.original.leadTelegramUsername}`
          : "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "tgId",
    header: () => <DataTableCenteredCell>TG ID лида</DataTableCenteredCell>,
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.leadTelegramId}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "curator",
    header: () => <DataTableCenteredCell>TG ID куратора</DataTableCenteredCell>,
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.curator?.tgId || "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "link",
    header: () => <DataTableCenteredCell>Лид ссылка</DataTableCenteredCell>,
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original?.leadLink?.name || "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "status",
    header: () => <LeadRequestStatusModal />,
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.status === LeadRequestStatus.PENDING
          ? "В ожидании"
          : row.original.status === LeadRequestStatus.APPROVED
            ? "Одобрено"
            : "Отклонен"}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "attachments",
    header: () => <DataTableCenteredCell>Пруфы</DataTableCenteredCell>,
    cell: ({ row }) => (
      <DataTableCenteredCell>
        <ProofsModal files={row.original.attachments} />
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "createdAt",
    header: () => (
      <DataTableCenteredCell>
        <SortButton title="Время подачи" sort="createdAt" />
      </DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.createdAt
          ? formatDate(new Date(row.original.createdAt))
          : "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    id: "actions",
    header: () => {
      return <DataTableCenteredCell>Действия</DataTableCenteredCell>;
    },
    cell: ({ row }) => {
      const { _id } = row.original;

      return (
        <DataTableCenteredCell>
          <ActionsDropdown id={_id} />
        </DataTableCenteredCell>
      );
    },
  },
];
